import { useState, useMemo, useRef, useEffect } from 'react';
import { descargarCsv, numeroCsv } from '@/utils/csv';
import { useInventorySummary, useStockAlerts, useStockMovements, useCreateStockMovement } from '@/hooks/useInventory';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { StockMovementCreate } from '@/services/inventory';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  Package,
  AlertTriangle,
  Calendar,
  TrendingDown,
  Plus,
  Minus,
  RefreshCw,
  Search,
  ArrowUp,
  ArrowDown,
  Clock,
  DollarSign,
  Filter,
  Wrench,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Download,
} from 'lucide-react';
import clsx from 'clsx';
import { formatPrice } from '@/utils/format';

const MOVEMENT_TYPES = [
  { value: 'purchase', label: 'Compra', icon: ArrowUp, color: 'text-green-600 bg-green-100' },
  { value: 'sale', label: 'Venta', icon: ArrowDown, color: 'text-blue-600 bg-blue-100' },
  { value: 'adjustment', label: 'Ajuste', icon: RefreshCw, color: 'text-gray-600 bg-gray-100' },
  { value: 'loss', label: 'Pérdida/Merma', icon: Minus, color: 'text-red-600 bg-red-100' },
  { value: 'expired', label: 'Vencido', icon: Calendar, color: 'text-orange-600 bg-orange-100' },
  { value: 'return', label: 'Devolución', icon: ArrowUp, color: 'text-purple-600 bg-purple-100' },
  { value: 'internal_use', label: 'Uso interno', icon: Minus, color: 'text-yellow-600 bg-yellow-100' },
];

const MOVEMENT_TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  ...MOVEMENT_TYPES.map(t => ({ value: t.value, label: t.label })),
];

export default function Inventory() {
  const [activeTab, setActiveTab] = useState<'summary' | 'alerts' | 'movements'>('summary');
  // Con cientos de alertas de stock bajo, las de vencimiento quedaban enterradas y no
  // habia forma de ver cuales eran. El filtro y las tarjetas clickeables las separan.
  const [alertFilter, setAlertFilter] = useState<'all' | 'low_stock' | 'expiration'>('all');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [movementFilter, setMovementFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);

  // Filtros para tabla de productos en resumen
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState('');
  const [inventoryStockFilter, setInventoryStockFilter] = useState<'all' | 'low' | 'ok'>('all');

  // Estado para ordenamiento de columnas
  type SortColumn = 'name' | 'category' | 'stock' | 'minimum_stock' | 'cost_price' | 'sale_price';
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Estado para edición inline de stock
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [editingStockValue, setEditingStockValue] = useState<string>('');
  const [savingStock, setSavingStock] = useState(false);
  const stockInputRef = useRef<HTMLInputElement>(null);

  // Estado del formulario de movimiento
  const [newMovement, setNewMovement] = useState<{
    product_id: number | null;
    product_name: string;
    product_current_stock: number;
    movement_type: string;
    quantity: string;
    unit_cost: string;
    batch_number: string;
    expiration_date: string;
    notes: string;
  }>({
    product_id: null,
    product_name: '',
    product_current_stock: 0,
    movement_type: 'purchase',
    quantity: '',
    unit_cost: '',
    batch_number: '',
    expiration_date: '',
    notes: '',
  });

  const { data: summary, isLoading: loadingSummary } = useInventorySummary();
  const { data: alerts, isLoading: loadingAlerts } = useStockAlerts();

  // Las de vencimiento van primero y ordenadas por urgencia: lo ya vencido arriba.
  const alertasFiltradas = useMemo(() => {
    const lista = (alerts ?? []).filter((a) =>
      alertFilter === 'all'
        ? true
        : alertFilter === 'low_stock'
          ? a.alert_type === 'low_stock'
          : a.alert_type === 'expired' || a.alert_type === 'expiring_soon'
    );
    if (alertFilter === 'low_stock') return lista;
    return [...lista].sort((a, b) => {
      const esVenc = (x: typeof a) => x.alert_type === 'expired' || x.alert_type === 'expiring_soon';
      if (esVenc(a) !== esVenc(b)) return esVenc(a) ? -1 : 1;
      if (!esVenc(a)) return 0;
      return (a.days_until_expiration ?? 9999) - (b.days_until_expiration ?? 9999);
    });
  }, [alerts, alertFilter]);
  const { data: movements, isLoading: loadingMovements } = useStockMovements({
    movement_type: movementFilter || undefined,
    limit: 100,
  });
  const { data: products, isLoading: loadingProducts } = useProducts({ only_active: true });
  const { data: categories = [] } = useCategories();
  const createMovement = useCreateStockMovement();

  // Filtrar y ordenar productos para la tabla de inventario
  const inventoryProducts = useMemo(() => {
    if (!products) return [];

    const filtered = products.filter(p => {
      // Solo productos con track_stock
      if (!p.track_stock) return false;

      // Filtro de búsqueda
      if (inventorySearch) {
        const search = inventorySearch.toLowerCase();
        if (!p.name.toLowerCase().includes(search) &&
            !p.code?.toLowerCase().includes(search)) {
          return false;
        }
      }

      // Filtro de categoría
      if (inventoryCategoryFilter && p.category_id !== parseInt(inventoryCategoryFilter)) {
        return false;
      }

      // Filtro de stock
      if (inventoryStockFilter === 'low' && Number(p.current_stock) > Number(p.minimum_stock)) {
        return false;
      }
      if (inventoryStockFilter === 'ok' && Number(p.current_stock) <= Number(p.minimum_stock)) {
        return false;
      }

      return true;
    });

    // Ordenar productos
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'category': {
          const catA = typeof a.category === 'object' ? a.category?.name : categories.find(c => c.id === a.category_id)?.name || '';
          const catB = typeof b.category === 'object' ? b.category?.name : categories.find(c => c.id === b.category_id)?.name || '';
          comparison = (catA || '').localeCompare(catB || '');
          break;
        }
        case 'stock':
          comparison = Number(a.current_stock) - Number(b.current_stock);
          break;
        case 'minimum_stock':
          comparison = Number(a.minimum_stock) - Number(b.minimum_stock);
          break;
        case 'cost_price':
          comparison = (a.cost_price || 0) - (b.cost_price || 0);
          break;
        case 'sale_price':
          comparison = (a.sale_price || 0) - (b.sale_price || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [products, inventorySearch, inventoryCategoryFilter, inventoryStockFilter, sortColumn, sortDirection, categories]);

  // Descargar el inventario (respeta los filtros actuales) como CSV que abre en Excel.
  // Incluye código, precios y cantidades para controlar precios y stock.
  const handleExportInventory = () => {
    const list = inventoryProducts;
    if (!list || list.length === 0) return;

    const catName = (id?: number | null) =>
      id == null ? '' : (categories.find((c) => c.id === id)?.name ?? '');

    descargarCsv(
      'inventario',
      ['Código', 'Código de barras', 'Producto', 'Categoría', 'Costo', 'Precio efectivo',
       'Precio tarjeta', 'Precio cuotas', 'Stock actual', 'Stock mínimo', 'Estado'],
      list.map((p) => {
        const low = p.track_stock && p.current_stock != null && p.minimum_stock != null &&
          Number(p.current_stock) <= Number(p.minimum_stock);
        return [
          p.code ?? '', p.barcode ?? '', p.name, catName(p.category_id),
          numeroCsv(p.cost_price), numeroCsv(p.sale_price), numeroCsv(p.list_price),
          numeroCsv(p.installment_price),
          numeroCsv(p.current_stock), numeroCsv(p.minimum_stock), low ? 'BAJO' : 'OK',
        ];
      }),
    );
  };

  // Filtrar productos por búsqueda
  const filteredProducts = useMemo(() => {
    if (!products || !productSearch.trim()) return [];
    const search = productSearch.toLowerCase();
    return products
      .filter(p =>
        p.track_stock &&
        (p.name.toLowerCase().includes(search) || p.code?.toLowerCase().includes(search))
      )
      .slice(0, 10);
  }, [products, productSearch]);

  const handleSelectProduct = (product: { id: number; name: string; current_stock: number }) => {
    setNewMovement({
      ...newMovement,
      product_id: product.id,
      product_name: product.name,
      product_current_stock: product.current_stock,
    });
    setProductSearch(product.name);
    setShowProductSearch(false);
  };

  // Función para manejar ordenamiento de columnas
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Componente para renderizar header con flecha de ordenamiento
  const SortableHeader = ({ column, label, align = 'left' }: { column: SortColumn; label: string; align?: 'left' | 'center' | 'right' }) => (
    <th
      className={clsx(
        'px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors select-none',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right'
      )}
      onClick={() => handleSort(column)}
    >
      <div className={clsx(
        'inline-flex items-center gap-1',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end'
      )}>
        {label}
        <span className="flex flex-col">
          <ChevronUp className={clsx(
            'h-3 w-3 -mb-1',
            sortColumn === column && sortDirection === 'asc' ? 'text-taupe-600' : 'text-gray-300'
          )} />
          <ChevronDown className={clsx(
            'h-3 w-3 -mt-1',
            sortColumn === column && sortDirection === 'desc' ? 'text-taupe-600' : 'text-gray-300'
          )} />
        </span>
      </div>
    </th>
  );

  // Funciones para edición inline de stock
  const handleStartEditStock = (productId: number, currentStock: number) => {
    setEditingStockId(productId);
    setEditingStockValue(currentStock.toString());
  };

  const handleCancelEditStock = () => {
    setEditingStockId(null);
    setEditingStockValue('');
  };

  const handleSaveStock = async (productId: number, currentStock: number) => {
    // Admite decimales (7,5 kg de un alimento a granel), con coma o con punto
    const newStock = parseFloat(editingStockValue.replace(',', '.'));
    if (isNaN(newStock) || newStock === currentStock) {
      handleCancelEditStock();
      return;
    }

    const quantity = newStock - currentStock;

    setSavingStock(true);
    try {
      await createMovement.mutateAsync({
        product_id: productId,
        movement_type: 'adjustment',
        quantity: quantity,
        notes: `Ajuste manual desde inventario: ${currentStock} → ${newStock}`,
      });
      handleCancelEditStock();
    } catch (error) {
      console.error('Error adjusting stock:', error);
    } finally {
      setSavingStock(false);
    }
  };

  // Focus en el input cuando se empieza a editar
  useEffect(() => {
    if (editingStockId !== null && stockInputRef.current) {
      stockInputRef.current.focus();
      stockInputRef.current.select();
    }
  }, [editingStockId]);

  const handleCreateMovement = async () => {
    if (!newMovement.product_id || !newMovement.quantity) return;

    const inputValue = parseInt(newMovement.quantity);
    let quantity: number;

    if (newMovement.movement_type === 'adjustment') {
      // Para ajustes: el usuario ingresa el NUEVO stock, calculamos el delta
      quantity = inputValue - newMovement.product_current_stock;
    } else {
      // Para otros tipos: la cantidad es directa (negativa para salidas)
      const isOutgoing = ['sale', 'loss', 'expired', 'internal_use'].includes(newMovement.movement_type);
      quantity = isOutgoing ? -Math.abs(inputValue) : Math.abs(inputValue);
    }

    const data: StockMovementCreate = {
      product_id: newMovement.product_id,
      movement_type: newMovement.movement_type as any,
      quantity: quantity,
      unit_cost: newMovement.unit_cost ? parseFloat(newMovement.unit_cost) : undefined,
      batch_number: newMovement.batch_number || undefined,
      expiration_date: newMovement.expiration_date || undefined,
      notes: newMovement.notes || undefined,
    };

    try {
      await createMovement.mutateAsync(data);
      setIsModalOpen(false);
      setNewMovement({
        product_id: null,
        product_name: '',
        product_current_stock: 0,
        movement_type: 'purchase',
        quantity: '',
        unit_cost: '',
        batch_number: '',
        expiration_date: '',
        notes: '',
      });
      setProductSearch('');
    } catch (error) {
      console.error('Error creating movement:', error);
    }
  };

  const getMovementTypeInfo = (type: string) => {
    return MOVEMENT_TYPES.find(t => t.value === type) || MOVEMENT_TYPES[2];
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'expiring_soon':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low_stock':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlertLabel = (type: string) => {
    switch (type) {
      case 'expired':
        return 'Vencido';
      case 'expiring_soon':
        return 'Por vencer';
      case 'low_stock':
        return 'Stock bajo';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventario</h1>
          <p className="text-gray-600">Gestión de stock y movimientos</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Registrar Movimiento
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('summary')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'summary'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Resumen
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
            activeTab === 'alerts'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Alertas
          {alerts && alerts.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {alerts.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('movements')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'movements'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Movimientos
        </button>
      </div>

      {/* Contenido */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {loadingSummary ? (
            <div className="text-center py-8">Cargando...</div>
          ) : summary ? (
            <>
              {/* Cards de resumen */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Productos</p>
                      <p className="text-2xl font-bold text-gray-900">{summary.total_products}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => { setAlertFilter('low_stock'); setActiveTab('alerts'); }}
                  className="card text-left hover:ring-2 hover:ring-yellow-300 transition"
                  title="Ver los productos con stock bajo"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Stock Bajo</p>
                      <p className="text-2xl font-bold text-yellow-600">{summary.low_stock_count}</p>
                    </div>
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <TrendingDown className="h-6 w-6 text-yellow-600" />
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => { setAlertFilter('expiration'); setActiveTab('alerts'); }}
                  className="card text-left hover:ring-2 hover:ring-red-300 transition"
                  title="Ver cuáles están vencidos o por vencer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Vencidos / Por Vencer</p>
                      <p className="text-2xl font-bold text-red-600">
                        {summary.expired_count} / {summary.expiring_soon_count}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">Tocá para ver cuáles</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-lg">
                      <Calendar className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </button>

                <div className="card">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500">Valor del Inventario</p>
                      <p
                        className="text-lg font-bold text-green-600 whitespace-nowrap"
                        title={formatPrice(summary.total_stock_value)}
                      >
                        {formatPrice(summary.total_stock_value)}
                      </p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Acciones rápidas */}
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {MOVEMENT_TYPES.slice(0, 4).map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => {
                          setNewMovement({ ...newMovement, movement_type: type.value });
                          setIsModalOpen(true);
                        }}
                        className={clsx(
                          'p-4 rounded-lg border-2 border-dashed transition-all hover:border-solid',
                          'flex flex-col items-center gap-2 text-center',
                          type.color.replace('text-', 'border-').replace('-600', '-300'),
                          'hover:' + type.color.replace('-100', '-200')
                        )}
                      >
                        <Icon className={clsx('h-6 w-6', type.color.split(' ')[0])} />
                        <span className="text-sm font-medium text-gray-700">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tabla de productos del inventario */}
              <div className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h3 className="font-semibold text-gray-900">Productos en Inventario</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Búsqueda */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                      />
                    </div>
                    {/* Filtro categoría */}
                    <select
                      value={inventoryCategoryFilter}
                      onChange={(e) => setInventoryCategoryFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Todas las categorías</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    {/* Filtro stock */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => setInventoryStockFilter('all')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'all'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Todos
                      </button>
                      <button
                        onClick={() => setInventoryStockFilter('low')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'low'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Stock bajo
                      </button>
                      <button
                        onClick={() => setInventoryStockFilter('ok')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'ok'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Stock OK
                      </button>
                    </div>
                    {/* Descargar inventario (precios y cantidades) - para todos los usuarios */}
                    <button
                      onClick={handleExportInventory}
                      disabled={inventoryProducts.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                      title="Descargar inventario (abre en Excel)"
                    >
                      <Download className="h-4 w-4" />
                      Descargar
                    </button>
                  </div>
                </div>

                {/* Tabla */}
                <div className="overflow-x-auto border rounded-lg">
                  {loadingProducts ? (
                    <div className="text-center py-8 text-gray-500">Cargando productos...</div>
                  ) : inventoryProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No se encontraron productos</p>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <SortableHeader column="name" label="Producto" align="left" />
                          <SortableHeader column="category" label="Categoría" align="left" />
                          <SortableHeader column="stock" label="Stock" align="center" />
                          <SortableHeader column="minimum_stock" label="Mín." align="center" />
                          <SortableHeader column="cost_price" label="P. Costo" align="right" />
                          <SortableHeader column="sale_price" label="P. Venta" align="right" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {inventoryProducts.slice(0, 50).map((product) => {
                          const isLowStock = Math.round(Number(product.current_stock)) < Math.round(Number(product.minimum_stock));
                          const categoryName = typeof product.category === 'object'
                            ? product.category?.name
                            : categories.find(c => c.id === product.category_id)?.name;

                          return (
                            <tr key={product.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className={clsx(
                                    'p-1.5 rounded-lg',
                                    product.is_service ? 'bg-purple-100' : 'bg-blue-100'
                                  )}>
                                    {product.is_service ? (
                                      <Wrench className="h-4 w-4 text-purple-600" />
                                    ) : (
                                      <Package className="h-4 w-4 text-blue-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{product.name}</p>
                                    {product.code && (
                                      <p className="text-xs text-gray-500 font-mono">{product.code}</p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {categoryName || '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {editingStockId === product.id ? (
                                  <div className="inline-flex items-center gap-1">
                                    <input
                                      ref={stockInputRef}
                                      type="text"
                                      inputMode="decimal"
                                      value={editingStockValue}
                                      onChange={(e) => setEditingStockValue(e.target.value.replace(/[^0-9.,]/g, ''))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleSaveStock(product.id, product.current_stock);
                                        } else if (e.key === 'Escape') {
                                          handleCancelEditStock();
                                        }
                                      }}
                                      className="w-20 px-2 py-1 text-center text-sm font-bold border-2 border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                                      disabled={savingStock}
                                    />
                                    <button
                                      onClick={() => handleSaveStock(product.id, product.current_stock)}
                                      disabled={savingStock}
                                      className="p-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors disabled:opacity-50"
                                      title="Confirmar"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={handleCancelEditStock}
                                      disabled={savingStock}
                                      className="p-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors disabled:opacity-50"
                                      title="Cancelar"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleStartEditStock(product.id, product.current_stock)}
                                    className={clsx(
                                      'inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all',
                                      Math.round(product.current_stock) < 0
                                        ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-300'
                                        : isLowStock
                                          ? Math.round(product.current_stock) === 0
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-orange-100 text-orange-700'
                                          : 'bg-green-100 text-green-700'
                                    )}
                                    title="Clic para ajustar stock"
                                  >
                                    {Math.round(product.current_stock)}
                                    {product.current_stock < 0 && ' ⚠️'}
                                  </button>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center text-gray-500">
                                {Math.round(product.minimum_stock)}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-600">
                                {product.cost_price != null ? formatPrice(product.cost_price) : '-'}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-green-600">
                                {product.sale_price != null ? formatPrice(product.sale_price) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                {inventoryProducts.length > 50 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    Mostrando 50 de {inventoryProducts.length} productos. Use los filtros para refinar la búsqueda.
                  </p>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="card">
          {/* Filtro por tipo: con cientos de alertas de stock bajo, las de vencimiento
              no se encontraban aunque estuvieran en la lista. */}
          <div className="flex flex-wrap gap-2 mb-4">
            {([
              { key: 'all' as const, label: 'Todas' },
              { key: 'expiration' as const, label: 'Vencidos y por vencer' },
              { key: 'low_stock' as const, label: 'Stock bajo' },
            ]).map((opcion) => {
              const cuantas = (alerts ?? []).filter((a) =>
                opcion.key === 'all'
                  ? true
                  : opcion.key === 'low_stock'
                    ? a.alert_type === 'low_stock'
                    : a.alert_type === 'expired' || a.alert_type === 'expiring_soon'
              ).length;
              return (
                <button
                  key={opcion.key}
                  type="button"
                  onClick={() => setAlertFilter(opcion.key)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    alertFilter === opcion.key
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {opcion.label}
                  <span className={clsx('ml-1.5', alertFilter === opcion.key ? 'text-white/70' : 'text-gray-400')}>
                    {cuantas}
                  </span>
                </button>
              );
            })}
          </div>

          {loadingAlerts ? (
            <div className="text-center py-8">Cargando...</div>
          ) : alertasFiltradas.length > 0 ? (
            <div className="space-y-3">
              {alertasFiltradas.map((alert) => (
                <div
                  key={`${alert.id}-${alert.alert_type}`}
                  className={clsx(
                    'p-4 rounded-lg border flex items-center justify-between',
                    getAlertColor(alert.alert_type)
                  )}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5" />
                    <div>
                      <p className="font-medium">{alert.name}</p>
                      {alert.code && <p className="text-sm opacity-75">{alert.code}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/50">
                      {getAlertLabel(alert.alert_type)}
                    </span>
                    <div className="mt-1 text-sm">
                      {alert.alert_type === 'low_stock' && (
                        <span>Stock: {alert.current_stock} / Mín: {alert.minimum_stock}</span>
                      )}
                      {(alert.alert_type === 'expired' || alert.alert_type === 'expiring_soon') && (
                        <span>
                          {alert.days_until_expiration !== undefined && alert.days_until_expiration < 0
                            ? `Venció hace ${Math.abs(alert.days_until_expiration)} días`
                            : `Vence en ${alert.days_until_expiration} días`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              {alertFilter === 'expiration'
                ? 'No hay productos vencidos ni por vencer'
                : alertFilter === 'low_stock'
                  ? 'No hay productos con stock bajo'
                  : 'No hay alertas de inventario'}
            </div>
          )}
        </div>
      )}

      {activeTab === 'movements' && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="card">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select
                options={MOVEMENT_TYPE_OPTIONS}
                value={movementFilter}
                onChange={(e) => setMovementFilter(e.target.value)}
                className="w-48"
              />
            </div>
          </div>

          {/* Lista de movimientos */}
          <div className="card overflow-hidden">
            {loadingMovements ? (
              <div className="text-center py-8">Cargando...</div>
            ) : movements && movements.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cantidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Stock
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Notas
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {movements.map((movement) => {
                      const typeInfo = getMovementTypeInfo(movement.movement_type);
                      const Icon = typeInfo.icon;
                      return (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              {formatDate(movement.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={clsx(
                              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                              typeInfo.color
                            )}>
                              <Icon className="h-3 w-3" />
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {movement.product_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={clsx(
                              'text-sm font-bold',
                              movement.quantity > 0 ? 'text-green-600' : 'text-red-600'
                            )}>
                              {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {movement.previous_stock} → {movement.new_stock}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {movement.notes || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No hay movimientos registrados
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de nuevo movimiento */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setProductSearch('');
        }}
        title="Registrar Movimiento de Stock"
        size="lg"
      >
        <div className="space-y-4">
          {/* Tipo de movimiento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Movimiento
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {MOVEMENT_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setNewMovement({ ...newMovement, movement_type: type.value })}
                    className={clsx(
                      'p-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1',
                      newMovement.movement_type === type.value
                        ? type.color + ' border-current'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Búsqueda de producto */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Producto
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setShowProductSearch(true);
                  if (!e.target.value) {
                    setNewMovement({ ...newMovement, product_id: null, product_name: '' });
                  }
                }}
                onFocus={() => setShowProductSearch(true)}
                placeholder="Buscar producto..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              />
            </div>

            {showProductSearch && filteredProducts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleSelectProduct(product)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{product.name}</span>
                      {product.code && (
                        <span className="text-xs text-gray-400">({product.code})</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      Stock: {product.current_stock}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cantidad */}
          {newMovement.movement_type === 'adjustment' ? (
            <div className="space-y-3">
              {newMovement.product_id && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-700">Stock actual:</span>
                    <span className="text-lg font-bold text-blue-800">{newMovement.product_current_stock}</span>
                  </div>
                </div>
              )}
              <Input
                type="number"
                min="0"
                label="Nuevo Stock"
                value={newMovement.quantity}
                onChange={(e) => setNewMovement({ ...newMovement, quantity: e.target.value })}
                placeholder="Ingrese el nuevo valor de stock"
              />
              {newMovement.product_id && newMovement.quantity && (
                <div className={clsx(
                  'p-3 rounded-lg border',
                  parseInt(newMovement.quantity) > newMovement.product_current_stock
                    ? 'bg-green-50 border-green-200'
                    : parseInt(newMovement.quantity) < newMovement.product_current_stock
                    ? 'bg-red-50 border-red-200'
                    : 'bg-gray-50 border-gray-200'
                )}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Diferencia:</span>
                    <span className={clsx(
                      'text-lg font-bold',
                      parseInt(newMovement.quantity) > newMovement.product_current_stock
                        ? 'text-green-600'
                        : parseInt(newMovement.quantity) < newMovement.product_current_stock
                        ? 'text-red-600'
                        : 'text-gray-600'
                    )}>
                      {parseInt(newMovement.quantity) - newMovement.product_current_stock > 0 ? '+' : ''}
                      {parseInt(newMovement.quantity) - newMovement.product_current_stock}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                min="1"
                label="Cantidad"
                value={newMovement.quantity}
                onChange={(e) => setNewMovement({ ...newMovement, quantity: e.target.value })}
                placeholder="Cantidad"
              />
              {newMovement.movement_type === 'purchase' && (
                <Input
                  type="number"
                  step="0.01"
                  label="Costo Unitario"
                  value={newMovement.unit_cost}
                  onChange={(e) => setNewMovement({ ...newMovement, unit_cost: e.target.value })}
                  placeholder="$0.00"
                />
              )}
            </div>
          )}

          {/* Lote y vencimiento */}
          {(newMovement.movement_type === 'purchase' || newMovement.movement_type === 'return') && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Número de Lote"
                value={newMovement.batch_number}
                onChange={(e) => setNewMovement({ ...newMovement, batch_number: e.target.value })}
                placeholder="Lote"
              />
              <Input
                type="date"
                label="Fecha de Vencimiento"
                value={newMovement.expiration_date}
                onChange={(e) => setNewMovement({ ...newMovement, expiration_date: e.target.value })}
              />
            </div>
          )}

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={newMovement.notes}
              onChange={(e) => setNewMovement({ ...newMovement, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setProductSearch('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateMovement}
              loading={createMovement.isPending}
              disabled={!newMovement.product_id || !newMovement.quantity}
            >
              Registrar Movimiento
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
